import { useState, useEffect, useCallback } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { User, Contribution, HistoryItem } from "@/types/common";
import ENDPOINTS from "../../api/endpoints";
import BottomNavBar from "@/common/BottomNavBar";
import useApiClient from "@/api/Client";
import UserInfoCard from "@/components/profile/UserInfoCard";
import MenuToggle from "@/components/profile/MenuToggle";
import DraftCard from "@/components/profile/DraftCard";
import ContributionCard from "@/components/profile/ContributionCard";
import RecentlyVisitedCard from "@/components/profile/RecentlyVisitedCard";
import GenericCardSkeleton from "@/shared/components/GenericCardSkeleton";
import { decodeToken } from "@/api/tokenManager";
import UserInfoCardSkeleton from "@/components/profile/UserInfoCardSkeleton";
import Badges from "@/components/profile/Badges";
import BadgesSkeleton from "@/components/profile/BadgesSkeleton";
import { useParams } from "react-router-dom";
import { useNotificationRefresh } from "@/hooks/useNotificationRefresh";
import MissingEmailModal from "@/components/auth/MissingEmailModal";
// import BadgeUnlockModal from "@/components/profile/BadgeUnlockModal";

const styles = {
  pageContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    maxWidth: "100%",
    overflowX: "hidden",
    bgcolor: "white",
    background: "linear-gradient(180deg, #7747FB 2.63%, #F2F2F2 25.87%)",
  },
  thumb: {
    width: 50,
    height: 50,
    borderRadius: 8,
    objectFit: "cover",
    border: "1px solid rgba(0,0,0,0.08)",
  },
};

const Dashboard = () => {
  const { api } = useApiClient();
  const { tab } = useParams();
  const [userData, setUserData] = useState<User | null>(null);
  const [showMissingEmailModal, setShowMissingEmailModal] = useState<boolean>(false);
  const [contributionData, setContributionData] = useState<Contribution[]>([]);
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const profileTab = localStorage.getItem("profileTab") || "contribution";
  const [activeTab, setActiveTab] = useState<"contribution" | "draft" | "recentlyVisited">(
    profileTab as "contribution" | "draft" | "recentlyVisited",
  );
  const { user_id } = decodeToken() ?? {};
  const isDraft = activeTab === "draft";
  const isRecentlyVisited = activeTab === "recentlyVisited";

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  // Check for missing email on load
  useEffect(() => {
    if (userData) {
      const emailPromptShown = sessionStorage.getItem("emailPromptShown");
      // Use logical check if email is missing
      if (!userData.email && !emailPromptShown) {
        setShowMissingEmailModal(true);
        sessionStorage.setItem("emailPromptShown", "true");
      }
    }
  }, [userData]);

  /*
  const [unlockedBadge, setUnlockedBadge] = useState<any>(null);
  const [badgeQueue, setBadgeQueue] = useState<any[]>([]);

  useEffect(() => {
    if (userData && userData.contribution_count !== undefined) {
      const currentCount = Number(userData.contribution_count);
      const storageKey = "lastSeenContributionCount";
      const lastSeenRaw = sessionStorage.getItem(storageKey);

      // Initialize if not present (first time user visits profile in this session)
      if (lastSeenRaw === null) {
        sessionStorage.setItem(storageKey, currentCount.toString());
        return;
      }

      const lastSeenCount = Number(lastSeenRaw);

      if (currentCount > lastSeenCount) {
        // Find ALL badges that were crossed in the gap
        const newlyUnlocked = badgesData.filter(
          (badge) => currentCount >= badge.target && lastSeenCount < badge.target
        );

        if (newlyUnlocked.length > 0) {
          // Add them to the queue to be shown one by one
          setBadgeQueue((prev) => [...prev, ...newlyUnlocked]);
        }

        // Update persistent seen count
        sessionStorage.setItem(storageKey, currentCount.toString());
      } else if (currentCount < lastSeenCount) {
        // Handle case where count might decrease (unlikely but safe to sync)
        sessionStorage.setItem(storageKey, currentCount.toString());
      }
    }
  }, [userData]);

  // Handle the sequential display of the queue
  useEffect(() => {
    if (!unlockedBadge && badgeQueue.length > 0) {
      const nextBadge = badgeQueue[0];
      setUnlockedBadge(nextBadge);
      setBadgeQueue((prev) => prev.slice(1));
    }
  }, [unlockedBadge, badgeQueue]);
  */

  const fetchUserProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const res = await api.get(ENDPOINTS.USERS.GET_BY_ID(String(user_id)));
      const { user } = res?.data?.data ?? {};
      setUserData(user);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setProfileLoading(false);
    }
  }, [api, user_id]);

  const fetchUserHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await api.get(ENDPOINTS.USER_HISTORY.GET_ALL, {
        user_id,
      });
      setHistoryData(res?.data?.data || []);
    } catch (error) {
      setHistoryData([]);
      console.error("Error fetching user history:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, user_id]);

  const fetchAllContributions = useCallback(async () => {
    if (isRecentlyVisited) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(ENDPOINTS.CONTRIBUTION.GET_ALL, {
        user_id,
        is_draft: isDraft,
      });

      setContributionData(res?.data?.data || []);
    } catch (error) {
      setContributionData([]);
      console.error("Error fetching user profile:", error);
    } finally {
      setLoading(false);
    }
  }, [api, user_id, isRecentlyVisited, isDraft]);

  // Real-time refresh when notifications arrive
  useNotificationRefresh(() => {
    fetchUserProfile();
    fetchAllContributions();
  });

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    fetchAllContributions();
  }, [activeTab, fetchAllContributions]);

  useEffect(() => {
    if (isRecentlyVisited) {
      fetchUserHistory();
    }
  }, [isRecentlyVisited, fetchUserHistory]);

  return (
    <>
      <Box sx={styles.pageContainer}>
        <Card
          sx={{
            background: "transparent",
            overflow: "auto",
            boxShadow: "0",
            width: "94%",
            mx: "auto",
          }}
        >
          {profileLoading ? (
            <>
              <UserInfoCardSkeleton />
              <BadgesSkeleton />
            </>
          ) : (
            <>
              <UserInfoCard data={userData} hideActions={true} />
              <Badges contributionCount={userData?.contribution_count || 0} />
            </>
          )}

          <Box sx={{ mt: "5%" }}>
              <MenuToggle
                activeTab={activeTab}
                setActiveTab={(tab) => {
                  setActiveTab(tab);
                  localStorage.setItem("profileTab", tab);
                }}
                isLoading={loading}
                reset={() => { }}
              />
          </Box>

          <CardContent
            sx={{
              p: 0,
              maxHeight: "90vh",
              overflowY: "auto",
              scrollbarWidth: "thin", // for Firefox
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: "6px",
              },
            }}
          >
            {isRecentlyVisited ? (
              historyLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <GenericCardSkeleton key={i} />
                ))
              ) : historyData.length > 0 ? (
                historyData.map((item: HistoryItem, idx: number) => (
                  <Box
                    key={item.history_id}
                    sx={{
                      mb:
                        historyData.length - 1 === idx
                          ? { xs: "120px", sm: "100px" }
                          : 0,
                    }}
                  >
                    <RecentlyVisitedCard
                      place={item.place}
                    />
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="#313131"
                  sx={{ textAlign: "center", py: 3, fontSize: 14 }}
                >
                  No recently visited places yet
                </Typography>
              )
            ) : loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <GenericCardSkeleton 
                  key={i} 
                  hasStatusLine={!isDraft}
                />
              ))
            ) : contributionData?.length > 0 ? (
              contributionData.map((c: Contribution, idx: number) =>
                isDraft ? (
                  <Box
                    key={c?.contribution_id}
                    sx={{
                      mb:
                        contributionData.length - 1 === idx
                          ? { xs: "15vw", sm: "100px" }
                          : "3.8%",
                    }}
                  >
                    <DraftCard
                      place={c?.place}
                      media={c?.media}
                      contributionId={c?.contribution_id}
                    />
                  </Box>
                ) : (
                  <Box
                    key={c?.submission_id}
                    sx={{
                      mb:
                        contributionData.length - 1 === idx
                          ? { xs: "15vw", sm: "100px" }
                          : "3.8%",
                    }}
                  >
                    <ContributionCard data={c} from="dashboard" />
                  </Box>
                ),
              )
            ) : (
              <Typography
                variant="body2"
                color="#313131"
                sx={{ textAlign: "center", py: 3, fontSize: 14 }}
              >
                {isDraft ? "No drafts yet" : "No contributions yet"}
              </Typography>
            )}
          </CardContent>
        </Card>

        <BottomNavBar />

        <MissingEmailModal
          open={showMissingEmailModal}
          onClose={() => setShowMissingEmailModal(false)}
        />
      </Box>

      {/* 
      <BadgeUnlockModal
        open={!!unlockedBadge}
        onClose={() => setUnlockedBadge(null)}
        badge={unlockedBadge}
      />
      */}
    </>
  );
};

export default Dashboard;
